import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Camera, AlertCircle, Wifi, WifiOff } from 'lucide-react';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import useSnapshotWithFallback from '../hooks/useSnapshotWithFallback';

/**
 * Optimized camera thumbnail with lazy loading and progressive enhancement
 */
const OptimizedCameraThumbnail = memo(({ 
  camera, 
  refreshKey = 0,
  size = 'medium', // 'small', 'medium', 'large'
  priority = 0, // Higher numbers = higher priority
  onError,
  onClick
}) => {
  const [loadingStage, setLoadingStage] = useState('idle'); // 'idle', 'placeholder', 'thumbnail', 'ready'
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // Intersection observer for lazy loading
  const [containerRef, isIntersecting, intersectionRatio] = useIntersectionObserver({
    threshold: [0, 0.1, 0.5],
    rootMargin: '100px' // Start loading when 100px away from viewport
  }, 150); // Debounce by 150ms

  // Progressive loading based on intersection ratio
  const shouldLoadPlaceholder = isIntersecting && intersectionRatio > 0;
  const shouldLoadThumbnail = isIntersecting && intersectionRatio > 0.1;
  const shouldPrioritizeLoading = intersectionRatio > 0.5;

  // Determine loading priority based on visibility and manual priority
  const effectivePriority = shouldPrioritizeLoading ? priority + 10 : priority;

  // Optimize snapshot options based on size and visibility
  const snapshotOptions = {
    maxRetries: shouldPrioritizeLoading ? 3 : 2,
    gifTimeout: shouldPrioritizeLoading ? 10000 : 6000,
    jpegTimeout: shouldPrioritizeLoading ? 5000 : 3000,
    gifDuration: size === 'small' ? 2 : size === 'large' ? 4 : 3,
    gifFps: size === 'small' ? 8 : size === 'large' ? 15 : 12
  };

  // Use snapshot hook only when we should load thumbnail
  const { src, loading, failed } = useSnapshotWithFallback(
    shouldLoadThumbnail ? camera._id : null,
    shouldLoadThumbnail ? refreshKey : 0,
    snapshotOptions
  );

  // Handle loading stages
  useEffect(() => {
    if (!shouldLoadPlaceholder) {
      setLoadingStage('idle');
      return;
    }

    if (shouldLoadThumbnail) {
      if (loading) {
        setLoadingStage('thumbnail');
      } else if (src && !failed) {
        setLoadingStage('ready');
        setHasError(false);
        setRetryCount(0);
      } else if (failed) {
        setLoadingStage('placeholder');
        setHasError(true);
        onError?.(camera._id, 'Thumbnail failed to load');
      }
    } else {
      setLoadingStage('placeholder');
    }
  }, [shouldLoadPlaceholder, shouldLoadThumbnail, loading, src, failed, camera._id, onError]);

  // Retry handler with exponential backoff
  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      setRetryCount(prev => prev + 1);
      setHasError(false);
      // Force refresh by incrementing refreshKey
      // This would need to be passed from parent component
    }
  }, [retryCount]);

  // Size configurations
  const sizeClasses = {
    small: {
      container: 'h-24 w-32',
      icon: 'h-6 w-6',
      text: 'text-xs',
      status: 'w-2 h-2'
    },
    medium: {
      container: 'h-32 w-48',
      icon: 'h-8 w-8',
      text: 'text-sm',
      status: 'w-2.5 h-2.5'
    },
    large: {
      container: 'h-48 w-64',
      icon: 'h-12 w-12',
      text: 'text-base',
      status: 'w-3 h-3'
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.medium;

  return (
    <div 
      ref={containerRef}
      className={`relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow ${currentSize.container}`}
      onClick={onClick}
      data-camera-id={camera._id}
      data-loading-stage={loadingStage}
    >
      {/* Connection Status Indicator */}
      <div className="absolute top-2 left-2 z-10">
        <div 
          className={`${currentSize.status} rounded-full ${
            camera.isOnline ? 'bg-green-500' : 'bg-red-500'
          }`}
          title={camera.isOnline ? 'Online' : 'Offline'}
        />
      </div>

      {/* Network Status Indicator */}
      <div className="absolute top-2 right-2 z-10">
        {loadingStage === 'ready' ? (
          <Wifi className={`${currentSize.icon} text-green-500 opacity-75`} />
        ) : loadingStage === 'thumbnail' ? (
          <div className="animate-spin">
            <Wifi className={`${currentSize.icon} text-blue-500 opacity-75`} />
          </div>
        ) : (
          <WifiOff className={`${currentSize.icon} text-gray-400 opacity-75`} />
        )}
      </div>

      {/* Content based on loading stage */}
      {loadingStage === 'idle' && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
          <div className="text-center">
            <Camera className={`${currentSize.icon} text-gray-400 mx-auto mb-1`} />
            <p className={`${currentSize.text} text-gray-500`}>Loading...</p>
          </div>
        </div>
      )}

      {loadingStage === 'placeholder' && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
          <div className="text-center">
            <Camera className={`${currentSize.icon} text-gray-400 mx-auto mb-1`} />
            <p className={`${currentSize.text} text-gray-500`}>
              {hasError ? 'Preview' : 'Camera'}
            </p>
            {hasError && retryCount < 3 && (
              <button 
                onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                className="mt-1 text-xs text-blue-600 hover:underline"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}

      {loadingStage === 'thumbnail' && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <Camera className={`${currentSize.icon} text-gray-400 mx-auto mb-1`} />
              <div className="absolute inset-0 animate-pulse bg-blue-500 opacity-20 rounded-full"></div>
            </div>
            <p className={`${currentSize.text} text-gray-500`}>Loading...</p>
          </div>
        </div>
      )}

      {loadingStage === 'ready' && src && (
        <>
          <img
            src={src}
            alt={`${camera.name} snapshot`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            style={{ imageRendering: size === 'small' ? 'pixelated' : 'auto' }}
            onError={() => {
              setHasError(true);
              setLoadingStage('placeholder');
              onError?.(camera._id, 'Image failed to load');
            }}
          />
          
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </>
      )}

      {/* Camera Info Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
        <h3 className={`${currentSize.text} font-medium truncate`} title={camera.name}>
          {camera.name}
        </h3>
        {size !== 'small' && camera.location?.exactLocation && (
          <p className="text-xs opacity-90 truncate" title={camera.location.exactLocation}>
            📍 {camera.location.exactLocation}
          </p>
        )}
      </div>

      {/* Performance Metrics (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-8 left-2 text-xs bg-black/50 text-white px-1 rounded">
          Stage: {loadingStage}
          <br />
          Priority: {effectivePriority}
          <br />
          Ratio: {intersectionRatio.toFixed(2)}
        </div>
      )}
    </div>
  );
});

OptimizedCameraThumbnail.displayName = 'OptimizedCameraThumbnail';

export default OptimizedCameraThumbnail;